import React, { useState, useEffect } from 'react';
import { Card } from '../components/Card';
import { Button } from '../components/Button';
import { Input } from '../components/Input';
import { showToast } from '../utils/toast';
import { Loading } from '../components/Loading';
import { userService } from '../services/userService';
import { useAuth } from '../contexts/AuthContext';
import { User, ChangePasswordData } from '../types';
import { format } from 'date-fns';

export const Profile: React.FC = () => {
  const { user: authUser } = useAuth();
  const [profile, setProfile] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [formData, setFormData] = useState({
    username: '',
    email: '',
  });
  const [passwordData, setPasswordData] = useState<ChangePasswordData & { confirm_password: string }>({
    current_password: '',
    new_password: '',
    confirm_password: '',
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    try {
      setLoading(true);
      const response = await userService.getProfile();
      if (response.success && response.data) {
        setProfile(response.data);
        setFormData({
          username: response.data.username,
          email: response.data.email || '',
        });
      } else {
        showToast.error(response.message || 'Failed to load profile');
      }
    } catch (err: any) {
      showToast.error(err.message || 'Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const updateData: any = {};
      if (formData.username !== profile?.username) {
        updateData.username = formData.username;
      }
      if (formData.email !== (profile?.email || '')) {
        updateData.email = formData.email || null;
      }

      if (Object.keys(updateData).length === 0) {
        showToast.warning('No changes to save');
        setSubmitting(false);
        return;
      }

      const response = await userService.updateProfile(updateData);
      if (response.success && response.data) {
        showToast.success('Profile updated successfully');
        setProfile(response.data);
        // Update auth context if username changed
        if (response.data.username !== authUser?.username) {
          // Reload page to update auth context
          window.location.reload();
        }
      } else {
        showToast.error(response.message || 'Failed to update profile');
      }
    } catch (err: any) {
      showToast.error(err.response?.data?.message || err.message || 'Failed to update profile');
    } finally {
      setSubmitting(false);
    }
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();

    if (passwordData.new_password !== passwordData.confirm_password) {
      showToast.error('New passwords do not match');
      return;
    }

    if (passwordData.new_password.length < 6) {
      showToast.error('New password must be at least 6 characters');
      return;
    }

    setSubmitting(true);

    try {
      const response = await userService.changePassword({
        current_password: passwordData.current_password,
        new_password: passwordData.new_password,
      });

      if (response.success) {
        showToast.success('Password changed successfully');
        setPasswordData({
          current_password: '',
          new_password: '',
          confirm_password: '',
        } as ChangePasswordData & { confirm_password: string });
        setShowPasswordForm(false);
      } else {
        showToast.error(response.message || 'Failed to change password');
      }
    } catch (err: any) {
      showToast.error(err.response?.data?.message || err.message || 'Failed to change password');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <Loading fullScreen text="Loading profile..." />;
  }

  if (!profile) {
    return (
      <div className="p-6">
        <div className="text-red-600">Failed to load profile</div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold text-gray-900">My Profile</h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Profile Information */}
        <Card title="Profile Information">
          <form onSubmit={handleUpdateProfile} className="space-y-4">
            <div className="relative">
              <label className="absolute left-0 top-0 text-xs text-gray-500 font-medium transform -translate-y-1">
                User ID
              </label>
              <input
                type="text"
                value={profile.id}
                readOnly
                className="w-full pt-6 pb-2 px-0 bg-transparent border-0 border-b-2 border-gray-300 text-gray-600 cursor-not-allowed"
              />
            </div>

            <Input
              label="Username"
              type="text"
              value={formData.username}
              onChange={(e) => setFormData({ ...formData, username: e.target.value })}
              required
            />

            <Input
              label="Email"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            />

            <div className="relative">
              <label className="absolute left-0 top-0 text-xs text-gray-500 font-medium transform -translate-y-1">
                Role
              </label>
              <input
                type="text"
                value={profile.role.charAt(0).toUpperCase() + profile.role.slice(1)}
                readOnly
                className="w-full pt-6 pb-2 px-0 bg-transparent border-0 border-b-2 border-gray-300 text-gray-600 cursor-not-allowed"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Account Created
              </label>
              <input
                type="text"
                value={format(new Date(profile.created_at), 'MMMM dd, yyyy')}
                readOnly
                className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-600 cursor-not-allowed"
              />
            </div>

            <Button type="submit" disabled={submitting}>
              {submitting ? 'Saving...' : 'Update Profile'}
            </Button>
          </form>
        </Card>

        {/* Change Password */}
        <Card title="Change Password">
          {!showPasswordForm ? (
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                Click the button below to change your password.
              </p>
              <Button
                type="button"
                variant="secondary"
                onClick={() => setShowPasswordForm(true)}
              >
                Change Password
              </Button>
            </div>
          ) : (
            <form onSubmit={handleChangePassword} className="space-y-4">
              <Input
                label="Current Password"
                type="password"
                value={passwordData.current_password}
                onChange={(e) =>
                  setPasswordData({ ...passwordData, current_password: e.target.value })
                }
                required
              />

              <Input
                label="New Password"
                type="password"
                value={passwordData.new_password}
                onChange={(e) =>
                  setPasswordData({ ...passwordData, new_password: e.target.value })
                }
                required
                minLength={6}
              />

              <Input
                label="Confirm New Password"
                type="password"
                value={passwordData.confirm_password}
                onChange={(e) =>
                  setPasswordData({ ...passwordData, confirm_password: e.target.value })
                }
                required
                minLength={6}
              />

              <div className="flex space-x-3">
                <Button type="submit" disabled={submitting}>
                  {submitting ? 'Changing...' : 'Change Password'}
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => {
                    setShowPasswordForm(false);
                    setPasswordData({
                      current_password: '',
                      new_password: '',
                      confirm_password: '',
                    } as ChangePasswordData & { confirm_password: string });
                  }}
                >
                  Cancel
                </Button>
              </div>
            </form>
          )}
        </Card>
      </div>
    </div>
  );
};

